import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import CrossfadeImage from './CrossfadeImage';
import SnapshotTileControls from './SnapshotTileControls';
import AllBreakoutRoomsContainer from '../../context/AllBreakoutRoomsContext';

const Container = styled.div`
    background-color: ${(props) => props.theme.colors.zoomGrey};
    position: relative;
    vertical-align: middle;
    align-self: center;
    overflow: hidden;
    display: inline-block;
    border: 1px solid ${(props) => props.theme.colors.zoomGrey};
    border-radius: 3px;
`;

const Title = styled.div`
    position: absolute;
    bottom: 5px;
    left: 5px;
    background-color: ${(props) => props.theme.colors.primary.main};
    color: ${(props) => props.theme.colors.white};
    padding: 3px 12px;
    border-radius: 4px;
    z-index: 2;
`;

export interface VideoTileProps {
    width: number;
    height: number;
    margin: number;
    breakoutName: string;
}

const SnapshotTile: React.FC<VideoTileProps> = ({ breakoutName, width, height, margin, ...rest }) => {
    const { imageData } = useContainer(AllBreakoutRoomsContainer);
    const { t } = useTranslation('all_breakout_rooms');

    const [isHovered, setIsHovered] = useState(false);
    const [title, setTitle] = useState(breakoutName);

    const src = useMemo(() => imageData.images[breakoutName]?.url, [imageData, breakoutName]);

    /**
     * Translate the `Main Classroom` breakout room
     * name for end-user display purposes
     */
    useEffect(() => {
        if (breakoutName === 'Main Classroom') {
            setTitle(t('main-classroom'));
            return;
        }
        setTitle(breakoutName);
    }, [t, breakoutName]);

    const onMouseEnter = () => {
        setIsHovered(true);
    };

    const onMouseLeave = () => {
        setIsHovered(false);
    };

    const style = useMemo(
        () => ({
            width: `${width}px`,
            height: `${height}px`,
            margin: `${margin}px`
        }),
        [height, margin, width]
    );

    return (
        <Container style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} {...rest}>
            <CrossfadeImage src={src} />
            <Title>{title}</Title>
            {isHovered && <SnapshotTileControls breakoutName={breakoutName} />}
        </Container>
    );
};

export default SnapshotTile;
